import {
  addReviewProjectMember,
  answerReviewProjectQuestionExternal,
  createAutoTagTaskGroup,
  createReviewProject,
  createReviewProjectAnalysis,
  createReviewProjectQuestion,
  createReviewProjectTag,
  deleteReviewProject,
  deleteReviewProjectAnalysis,
  deleteReviewProjectQuestion,
  deleteReviewProjectTag,
  getReviewProject,
  getReviewProjectAnalysisStatistics,
  getReviewProjectFileStatistics,
  getReviewProjectQuestionStatistics,
  getReviewProjectReport,
  getReviewProjectSeenStatistics,
  getReviewProjectTagStatistics,
  getReviewProjectTaskGroup,
  listReviewProjectAnalyses,
  listReviewProjectMembers,
  listReviewProjectNonPlatformUsers,
  listReviewProjectTaskGroups,
  markReviewProjectFilePageSeen,
  removeReviewProjectMember,
  removeReviewProjectNonPlatformUser,
  searchReviewProjectAnalysisData,
  searchReviewProjectFiles,
  searchReviewProjectQuestions,
  searchReviewProjectQuestionsExternal,
  searchReviewProjects,
  searchReviewProjectTags,
  setReviewProjectReport,
  updateReviewProject,
  updateReviewProjectAnalysis,
  updateReviewProjectFile,
  updateReviewProjectQuestion,
  updateReviewProjectTag,
  upsertReviewProjectNonPlatformUser,
  type FilterableSearchQuery,
  type PlaybookRunSearchQuery,
  type PlaybookRunSearchResult,
  type ReviewProjectAnalysisCreate,
  type ReviewProjectAnalysisInDb,
  type ReviewProjectAnalysisStatistics,
  type ReviewProjectAnalysisUpdate,
  type ReviewProjectAutoTagTaskGroupCreate,
  type ReviewProjectAutoTagTaskGroupInDb,
  type ReviewProjectAutoTagTaskGroupSummary,
  type ReviewProjectCreateUser,
  type ReviewProjectFileInDb,
  type ReviewProjectFilePageViewInDb,
  type ReviewProjectFileSearchResult,
  type ReviewProjectFileStatistics,
  type ReviewProjectFileUpdate,
  type ReviewProjectInDb,
  type ReviewProjectInDbDetail,
  type ReviewProjectNonPlatformUserLinkInDb,
  type ReviewProjectNonPlatformUserLinkUpsert,
  type ReviewProjectQuestionCreate,
  type ReviewProjectQuestionExternalSearchResult,
  type ReviewProjectQuestionInDb,
  type ReviewProjectQuestionSearchResult,
  type ReviewProjectQuestionStatistics,
  type ReviewProjectQuestionUpdate,
  type ReviewProjectReportInDb,
  type ReviewProjectReportTaskGroupInDb,
  type ReviewProjectReportTaskGroupSummary,
  type ReviewProjectSearchResult,
  type ReviewProjectSeenStatistics,
  type ReviewProjectTagCreate,
  type ReviewProjectTagInDb,
  type ReviewProjectTagSearchResult,
  type ReviewProjectTagStatistics,
  type ReviewProjectTagUpdate,
  type ReviewProjectUpdate,
  type UserInDb,
} from '@/api';
import { extractErrorDetail } from '@/utils/errorHandler';

export interface ReviewProjectListQuery {
  text?: string;
  page?: number;
  pageSize?: number;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface ReviewProjectFilesQuery {
  text?: string;
  page?: number;
  pageSize?: number;
  columnFilters?: Record<string, string[]>;
}

export interface ReviewProjectTagsQuery {
  text?: string;
  page?: number;
  pageSize?: number;
}

export interface ReviewProjectAnalysisDataQuery {
  page?: number;
  pageSize?: number;
}

// Task groups come in per-type variants discriminated on task_type; the list and
// get endpoints return the union, so callers narrow before touching subtype fields.
export type ReviewProjectTaskGroupSummary =
  ReviewProjectAutoTagTaskGroupSummary | ReviewProjectReportTaskGroupSummary;

export type ReviewProjectTaskGroupDetail =
  ReviewProjectAutoTagTaskGroupInDb | ReviewProjectReportTaskGroupInDb;

export interface ReviewProjectQuestionsQuery {
  text?: string;
  page?: number;
  pageSize?: number;
  /** undefined = all; true/false maps to the 'answered' column filter. */
  answered?: boolean;
}

/**
 * The external-access JWT was rejected (expired or revoked). Callers clear the
 * external session and send the visitor back to the request-access form.
 */
export class ExternalSessionExpiredError extends Error {
  constructor() {
    super('Your session has expired. Please request a new access link.');
  }
}

function buildQuestionsSearchBody(query: ReviewProjectQuestionsQuery): FilterableSearchQuery {
  const pageSize = query.pageSize ?? 10;
  const page = query.page ?? 1;
  return {
    text: query.text ?? '',
    limit: pageSize,
    offset: (page - 1) * pageSize,
    sort_key: 'created_at',
    sort_direction: 'desc',
    column_filters:
      query.answered === undefined ? undefined : { answered: [String(query.answered)] },
  };
}

export const ReviewProjectService = {
  async search(query: ReviewProjectListQuery = {}): Promise<ReviewProjectSearchResult> {
    try {
      const pageSize = query.pageSize ?? 10;
      const page = query.page ?? 1;
      const body: FilterableSearchQuery = {
        text: query.text ?? '',
        limit: pageSize,
        offset: (page - 1) * pageSize,
        sort_key: query.sortKey ?? 'created_at',
        sort_direction: query.sortDirection ?? 'desc',
      };

      const response = await searchReviewProjects({ body });
      if (!response.data) {
        throw new Error('Failed to search review projects');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to search review projects.'));
    }
  },

  async searchFiles(
    projectUid: string,
    query: ReviewProjectFilesQuery = {}
  ): Promise<ReviewProjectFileSearchResult> {
    try {
      const pageSize = query.pageSize ?? 10;
      const page = query.page ?? 1;
      const body: FilterableSearchQuery = {
        text: query.text ?? '',
        limit: pageSize,
        offset: (page - 1) * pageSize,
        column_filters: query.columnFilters,
      };

      const response = await searchReviewProjectFiles({
        path: { project_uid: projectUid },
        body,
      });
      if (!response.data) {
        throw new Error('Failed to search review project files');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to search review project files.'));
    }
  },

  async updateFile(
    projectUid: string,
    fileUid: string,
    payload: ReviewProjectFileUpdate
  ): Promise<ReviewProjectFileInDb> {
    try {
      const response = await updateReviewProjectFile({
        path: { project_uid: projectUid, file_uid: fileUid },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to update review project file');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to update review project file.'));
    }
  },

  async markFilePageSeen(
    projectUid: string,
    revisionUid: string,
    pageNumber: number
  ): Promise<ReviewProjectFilePageViewInDb> {
    try {
      const response = await markReviewProjectFilePageSeen({
        path: { project_uid: projectUid, page_number: pageNumber },
        body: { vault_file_revision_uid: revisionUid },
      });
      if (!response.data) {
        throw new Error('Failed to mark page as seen');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to mark page as seen.'));
    }
  },

  async getById(uid: string): Promise<ReviewProjectInDbDetail> {
    try {
      const response = await getReviewProject({ path: { uid } });
      if (!response.data) {
        throw new Error('Failed to fetch review project');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to fetch review project.'));
    }
  },

  async create(payload: ReviewProjectCreateUser): Promise<ReviewProjectInDb> {
    try {
      const response = await createReviewProject({ body: payload });
      if (!response.data) {
        throw new Error('Failed to create review project');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to create review project.'));
    }
  },

  async update(uid: string, payload: ReviewProjectUpdate): Promise<ReviewProjectInDb> {
    try {
      const response = await updateReviewProject({ path: { uid }, body: payload });
      if (!response.data) {
        throw new Error('Failed to update review project');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to update review project.'));
    }
  },

  async delete(uid: string): Promise<ReviewProjectInDb> {
    try {
      const response = await deleteReviewProject({ path: { uid } });
      if (!response.data) {
        throw new Error('Failed to delete review project');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to delete review project.'));
    }
  },

  async listMembers(uid: string): Promise<UserInDb[]> {
    try {
      const response = await listReviewProjectMembers({ path: { uid } });
      if (!response.data) {
        throw new Error('Failed to fetch review project members');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to fetch review project members.'));
    }
  },

  async addMember(uid: string, userUid: string): Promise<ReviewProjectInDb> {
    try {
      const response = await addReviewProjectMember({ path: { uid, user_uid: userUid } });
      if (!response.data) {
        throw new Error('Failed to add review project member');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to add review project member.'));
    }
  },

  async removeMember(uid: string, userUid: string): Promise<ReviewProjectInDb> {
    try {
      const response = await removeReviewProjectMember({ path: { uid, user_uid: userUid } });
      if (!response.data) {
        throw new Error('Failed to remove review project member');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to remove review project member.'));
    }
  },

  async listExternalUsers(uid: string): Promise<ReviewProjectNonPlatformUserLinkInDb[]> {
    try {
      const response = await listReviewProjectNonPlatformUsers({ path: { uid } });
      if (!response.data) {
        throw new Error('Failed to fetch external users');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to fetch external users.'));
    }
  },

  async upsertExternalUser(
    uid: string,
    payload: ReviewProjectNonPlatformUserLinkUpsert
  ): Promise<ReviewProjectNonPlatformUserLinkInDb> {
    try {
      const response = await upsertReviewProjectNonPlatformUser({ path: { uid }, body: payload });
      if (!response.data) {
        throw new Error('Failed to save external user');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to save external user.'));
    }
  },

  async removeExternalUser(uid: string, nonPlatformUserUid: string): Promise<void> {
    try {
      await removeReviewProjectNonPlatformUser({
        path: { uid, non_platform_user_uid: nonPlatformUserUid },
      });
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to remove external user.'));
    }
  },

  async searchQuestions(
    projectUid: string,
    query: ReviewProjectQuestionsQuery = {}
  ): Promise<ReviewProjectQuestionSearchResult> {
    try {
      const response = await searchReviewProjectQuestions({
        path: { project_uid: projectUid },
        body: buildQuestionsSearchBody(query),
      });
      if (!response.data) {
        throw new Error('Failed to search review project questions');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to search review project questions.'));
    }
  },

  async createQuestion(
    projectUid: string,
    payload: ReviewProjectQuestionCreate
  ): Promise<ReviewProjectQuestionInDb> {
    try {
      const response = await createReviewProjectQuestion({
        path: { project_uid: projectUid },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to create question');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to create question.'));
    }
  },

  async updateQuestion(
    projectUid: string,
    questionUid: string,
    payload: ReviewProjectQuestionUpdate
  ): Promise<ReviewProjectQuestionInDb> {
    try {
      const response = await updateReviewProjectQuestion({
        path: { project_uid: projectUid, question_uid: questionUid },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to update question');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to update question.'));
    }
  },

  async deleteQuestion(
    projectUid: string,
    questionUid: string
  ): Promise<ReviewProjectQuestionInDb> {
    try {
      const response = await deleteReviewProjectQuestion({
        path: { project_uid: projectUid, question_uid: questionUid },
      });
      if (!response.data) {
        throw new Error('Failed to delete question');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to delete question.'));
    }
  },

  /**
   * Question search for external (non-platform) visitors; same semantics as
   * searchQuestions but addressed by the public external uid and authenticated
   * with the external-access JWT passed per request.
   */
  async searchQuestionsExternal(
    externalUid: string,
    query: ReviewProjectQuestionsQuery,
    accessToken: string
  ): Promise<ReviewProjectQuestionExternalSearchResult> {
    const { data, error, response } = await searchReviewProjectQuestionsExternal({
      path: { external_uid: externalUid },
      body: buildQuestionsSearchBody(query),
      auth: accessToken,
      throwOnError: false,
    });
    if (data) return data;
    if (response?.status === 401) throw new ExternalSessionExpiredError();
    throw new Error(extractErrorDetail(error, 'Failed to load questions.'));
  },

  /**
   * Answer an open question as an external visitor. The backend records the
   * requesting non-platform user as answerer and rejects with 409 when the
   * question is already answered (someone else won the race).
   */
  async answerQuestionExternal(
    externalUid: string,
    questionUid: string,
    answer: string,
    accessToken: string
  ): Promise<'ok' | 'already-answered'> {
    const { data, error, response } = await answerReviewProjectQuestionExternal({
      path: { external_uid: externalUid, question_uid: questionUid },
      body: { answer },
      auth: accessToken,
      throwOnError: false,
    });
    if (data) return 'ok';
    if (response?.status === 409) return 'already-answered';
    if (response?.status === 401) throw new ExternalSessionExpiredError();
    throw new Error(extractErrorDetail(error, 'Failed to save the answer.'));
  },

  async getReport(projectUid: string): Promise<ReviewProjectReportInDb> {
    try {
      const response = await getReviewProjectReport({
        path: { project_uid: projectUid },
      });
      if (!response.data) {
        throw new Error('Failed to fetch review project report');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to fetch review project report.'));
    }
  },

  async setReport(projectUid: string, content: string): Promise<ReviewProjectReportInDb> {
    try {
      const response = await setReviewProjectReport({
        path: { project_uid: projectUid },
        body: { content },
      });
      if (!response.data) {
        throw new Error('Failed to save review project report');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to save review project report.'));
    }
  },

  async getQuestionStatistics(projectUid: string): Promise<ReviewProjectQuestionStatistics> {
    try {
      const response = await getReviewProjectQuestionStatistics({
        path: { project_uid: projectUid },
      });
      if (!response.data) {
        throw new Error('Failed to fetch question statistics');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to fetch question statistics.'));
    }
  },

  async getFileStatistics(projectUid: string): Promise<ReviewProjectFileStatistics> {
    try {
      const response = await getReviewProjectFileStatistics({
        path: { project_uid: projectUid },
      });
      if (!response.data) {
        throw new Error('Failed to fetch file statistics');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to fetch file statistics.'));
    }
  },

  async getSeenStatistics(projectUid: string): Promise<ReviewProjectSeenStatistics> {
    try {
      const response = await getReviewProjectSeenStatistics({
        path: { project_uid: projectUid },
      });
      if (!response.data) {
        throw new Error('Failed to fetch review coverage statistics');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to fetch review coverage statistics.'));
    }
  },

  async getTagStatistics(projectUid: string): Promise<ReviewProjectTagStatistics> {
    try {
      const response = await getReviewProjectTagStatistics({
        path: { project_uid: projectUid },
      });
      if (!response.data) {
        throw new Error('Failed to fetch tag statistics');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to fetch tag statistics.'));
    }
  },

  async getAnalysisStatistics(projectUid: string): Promise<ReviewProjectAnalysisStatistics> {
    try {
      const response = await getReviewProjectAnalysisStatistics({
        path: { project_uid: projectUid },
      });
      if (!response.data) {
        throw new Error('Failed to fetch analysis statistics');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to fetch analysis statistics.'));
    }
  },

  async listAnalyses(projectUid: string): Promise<ReviewProjectAnalysisInDb[]> {
    try {
      const response = await listReviewProjectAnalyses({ path: { project_uid: projectUid } });
      if (!response.data) {
        throw new Error('Failed to fetch review project analyses');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to fetch review project analyses.'));
    }
  },

  async listTaskGroups(projectUid: string): Promise<ReviewProjectTaskGroupSummary[]> {
    try {
      const response = await listReviewProjectTaskGroups({ path: { project_uid: projectUid } });
      if (!response.data) {
        throw new Error('Failed to fetch review project task groups');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to fetch review project task groups.'));
    }
  },

  async getTaskGroup(
    projectUid: string,
    taskGroupUid: string
  ): Promise<ReviewProjectTaskGroupDetail> {
    try {
      const response = await getReviewProjectTaskGroup({
        path: { project_uid: projectUid, task_group_uid: taskGroupUid },
      });
      if (!response.data) {
        throw new Error('Failed to fetch review project task group');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to fetch review project task group.'));
    }
  },

  async createAutoTagTaskGroup(
    projectUid: string,
    payload: ReviewProjectAutoTagTaskGroupCreate
  ): Promise<ReviewProjectAutoTagTaskGroupInDb> {
    try {
      const response = await createAutoTagTaskGroup({
        path: { project_uid: projectUid },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to start auto-tagging');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to start auto-tagging.'));
    }
  },

  async createAnalysis(
    projectUid: string,
    payload: ReviewProjectAnalysisCreate
  ): Promise<ReviewProjectAnalysisInDb> {
    try {
      const response = await createReviewProjectAnalysis({
        path: { project_uid: projectUid },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to create review project analysis');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to create review project analysis.'));
    }
  },

  async updateAnalysis(
    projectUid: string,
    analysisUid: string,
    payload: ReviewProjectAnalysisUpdate
  ): Promise<ReviewProjectAnalysisInDb> {
    try {
      const response = await updateReviewProjectAnalysis({
        path: { project_uid: projectUid, analysis_uid: analysisUid },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to update review project analysis');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to update review project analysis.'));
    }
  },

  async deleteAnalysis(
    projectUid: string,
    analysisUid: string
  ): Promise<ReviewProjectAnalysisInDb> {
    try {
      const response = await deleteReviewProjectAnalysis({
        path: { project_uid: projectUid, analysis_uid: analysisUid },
      });
      if (!response.data) {
        throw new Error('Failed to delete review project analysis');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to delete review project analysis.'));
    }
  },

  async searchAnalysisData(
    projectUid: string,
    analysisUid: string,
    query: ReviewProjectAnalysisDataQuery = {}
  ): Promise<PlaybookRunSearchResult> {
    try {
      const pageSize = query.pageSize ?? 50;
      const page = query.page ?? 1;
      const body: PlaybookRunSearchQuery = {
        text: '',
        limit: pageSize,
        offset: (page - 1) * pageSize,
        sort_key: 'created_at',
        sort_direction: 'desc',
      };

      const response = await searchReviewProjectAnalysisData({
        path: { project_uid: projectUid, analysis_uid: analysisUid },
        body,
      });
      if (!response.data) {
        throw new Error('Failed to search analysis data');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to search analysis data.'));
    }
  },

  async searchTags(
    projectUid: string,
    query: ReviewProjectTagsQuery = {}
  ): Promise<ReviewProjectTagSearchResult> {
    try {
      const pageSize = query.pageSize ?? 50;
      const page = query.page ?? 1;
      const body: FilterableSearchQuery = {
        text: query.text ?? '',
        limit: pageSize,
        offset: (page - 1) * pageSize,
        sort_key: 'name',
        sort_direction: 'asc',
      };

      const response = await searchReviewProjectTags({
        path: { project_uid: projectUid },
        body,
      });
      if (!response.data) {
        throw new Error('Failed to search review project tags');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to search review project tags.'));
    }
  },

  async createTag(
    projectUid: string,
    payload: ReviewProjectTagCreate
  ): Promise<ReviewProjectTagInDb> {
    try {
      const response = await createReviewProjectTag({
        path: { project_uid: projectUid },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to create review project tag');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to create review project tag.'));
    }
  },

  async updateTag(
    projectUid: string,
    tagUid: string,
    payload: ReviewProjectTagUpdate
  ): Promise<ReviewProjectTagInDb> {
    try {
      const response = await updateReviewProjectTag({
        path: { project_uid: projectUid, tag_uid: tagUid },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to update review project tag');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to update review project tag.'));
    }
  },

  async deleteTag(projectUid: string, tagUid: string): Promise<ReviewProjectTagInDb> {
    try {
      const response = await deleteReviewProjectTag({
        path: { project_uid: projectUid, tag_uid: tagUid },
      });
      if (!response.data) {
        throw new Error('Failed to delete review project tag');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to delete review project tag.'));
    }
  },
};
